import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Building2, Plus, Pencil, Trash2, KeyRound, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import { BranchSelect, BranchTag, useBranches } from "@/components/BranchBits";

function ClientForm({ open, onOpenChange, editing, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", contact_name: "", phone: "", industry: "",
    address: "", website: "", logo_url: "", notes: "",
    login_email: "", login_password: "", branch_id: "",
  });

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          name: editing.name || "", email: editing.email || "",
          contact_name: editing.contact_name || "", phone: editing.phone || "",
          industry: editing.industry || "", address: editing.address || "",
          website: editing.website || "", logo_url: editing.logo_url || "",
          notes: editing.notes || "",
          login_email: "", login_password: "", branch_id: editing.branch_id || "",
        });
      } else {
        setForm({ name: "", email: "", contact_name: "", phone: "", industry: "", address: "", website: "", logo_url: "", notes: "", login_email: "", login_password: "", branch_id: "" });
      }
    }
  }, [open, editing]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Company name is required");
    // Portal login is optional — but it's an all-or-nothing pair.
    const le = (form.login_email || "").trim();
    const lp = (form.login_password || "").trim();
    if (!editing) {
      if (le && !lp) return toast.error("Set a password to enable the portal login (or clear the email to skip)");
      if (lp && !le) return toast.error("Set a login email to enable the portal login (or clear the password to skip)");
      if (lp && lp.length < 8) return toast.error("Portal password must be at least 8 characters");
    } else {
      // Edit mode — client may already have a login. We only touch it when
      // the admin fills a value; blank = leave as-is.
      if (lp && lp.length < 8) return toast.error("Portal password must be at least 8 characters");
    }
    // Strip empty-string fields so Optional[EmailStr] / Optional[str] on the
    // backend don't fail validation. Only `name` is required.
    const payload = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)
    );
    // Only the Company Admin picks the branch (blank = company-wide); Branch Admins are fixed to their own.
    if (user?.role === "admin") payload.branch_id = form.branch_id || null;
    else delete payload.branch_id;
    try {
      if (editing) {
        await api.patch(`/clients/${editing.id}`, payload);
        toast.success("Client updated");
      } else {
        await api.post("/clients", payload);
        toast.success("Client added");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="client-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Client" : "Add Client"}</DialogTitle>
          <DialogDescription>
            Create a client organisation and optionally issue login credentials so they can access their own dashboard.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Company name *</Label><Input data-testid="client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Primary email</Label><Input type="email" data-testid="client-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Contact person</Label><Input data-testid="client-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div><Label>Phone</Label><Input data-testid="client-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Industry</Label><Input data-testid="client-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
            <div><Label>Website</Label><Input data-testid="client-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" /></div>
          </div>
          <BranchSelect value={form.branch_id} onChange={(v) => setForm({ ...form, branch_id: v })} />
          <div><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

          <div className="pt-3 border-t border-border/50">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound className="w-4 h-4 text-primary" />
              <div className="font-medium text-sm">Client Portal Login</div>
              {editing?.has_login && <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-500">Active</Badge>}
            </div>
            <div className="text-xs text-muted-foreground mb-2">
              Both fields together enable the client portal login. Leave <b>both</b> blank to skip; leave blank in edit mode to keep the existing password.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Login email</Label><Input type="email" data-testid="client-login-email" value={form.login_email} onChange={(e) => setForm({ ...form, login_email: e.target.value })} placeholder={editing?.has_login ? "(unchanged)" : "client@company.com"} /></div>
              <div><Label>Password{editing?.has_login ? " (reset)" : ""}</Label><Input type="password" data-testid="client-login-password" value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} placeholder={editing?.has_login ? "(unchanged)" : "min 8 chars"} /></div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="client-save" onClick={save}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const { user } = useAuth();
  const canCreate = user?.role === "admin" || user?.role === "branch_admin";
  const { byId: branchById } = useBranches();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/clients").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this client? Projects linked to it must be removed first.")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const totalWithLogin = rows.filter((c) => c.has_login).length;
  const totalProjects = rows.reduce((a, c) => a + (c.project_count || 0), 0);

  const columns = [
    {
      key: "name", label: "Client", sortable: true, render: (r) => (
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Building2 className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate max-w-[220px]">{r.name}</div>
            {r.industry && <div className="text-[10px] text-muted-foreground truncate max-w-[220px]">{r.industry}</div>}
          </div>
        </div>
      ),
    },
    { key: "contact_name", label: "Contact", sortable: true, render: (r) => (
      <div className="text-sm">
        <div>{r.contact_name || "—"}</div>
        {r.email && <div className="text-[10px] text-muted-foreground">{r.email}</div>}
      </div>
    ) },
    { key: "phone", label: "Phone", sortable: true, render: (r) => r.phone || <span className="text-muted-foreground">—</span> },
    { key: "project_count", label: "Projects", sortable: true, className: "number", render: (r) => (
      <span className="number">{r.project_count || 0}</span>
    ) },
    { key: "branch_id", label: "Branch", sortable: true, render: (r) => <BranchTag id={r.branch_id} byId={branchById} /> },
    { key: "has_login", label: "Login", sortable: true, render: (r) => (
      r.has_login
        ? <Badge className="bg-emerald-500/15 text-emerald-500 border-0 rounded-md text-[10px]">Enabled</Badge>
        : <Badge variant="outline" className="text-[10px]">Off</Badge>
    ) },
    { key: "is_active", label: "Status", sortable: true, render: (r) => (
      r.is_active
        ? <Badge className="bg-primary/15 text-primary border-0 rounded-md text-[10px]">Active</Badge>
        : <Badge variant="outline" className="text-[10px]">Inactive</Badge>
    ) },
  ];

  const rowActions = (r) => !r.can_modify ? (
    <div className="text-[10px] text-muted-foreground text-right pr-2" title="Created by another administrator">view only</div>
  ) : (
    <div className="flex items-center justify-end gap-1">
      <Button data-testid={`edit-client-${r.id}`} size="sm" variant="ghost" onClick={() => { setEditing(r); setOpen(true); }}>
        <Pencil className="w-3 h-3" />
      </Button>
      <Button data-testid={`delete-client-${r.id}`} size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-red-500 hover:text-red-500" disabled={(r.project_count || 0) > 0} title={(r.project_count || 0) > 0 ? "Detach projects first" : undefined}>
        <Trash2 className="w-3 h-3" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        testid="clients-header"
        title="Clients"
        subtitle={`${rows.length} client organisations`}
        actions={canCreate && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" onClick={() => setEditing(null)}><Plus className="w-4 h-4 mr-1" />Add Client</Button>
            </DialogTrigger>
            <ClientForm open={open} onOpenChange={setOpen} editing={editing} onSaved={load} />
          </Dialog>
        )}
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Clients" value={rows.length} icon={Building2} tone="primary" />
        <KpiCard label="With Login" value={totalWithLogin} icon={KeyRound} />
        <KpiCard label="Total Projects" value={totalProjects} icon={FolderKanban} />
        <KpiCard label="Active" value={rows.filter((c) => c.is_active).length} />
      </div>
      <DataTable
        testid="clients-table"
        columns={columns}
        data={rows}
        loading={loading}
        empty="No clients yet — add your first client to link them to a project."
        searchKeys={["name", "email", "contact_name", "phone", "industry"]}
        initialPageSize={10}
        rowActions={rowActions}
      />
    </div>
  );
}
